/**
 * Post–birthday welcome: context, FAQ, and Start check-in (Figma step 01).
 * @agentic members
 */

import { Paper, Stack, Title, Text, Button, Divider, Box } from "@mantine/core";

import { SurveyWordmark } from "./SurveyWordmark";
import { getAdvocateDisplayName } from "./surveyEnv";

type WelcomeContext = {
  checkInNumber?: number;
  advocateDisplayName?: string;
  advocateTitle?: string;
  advocateMessage?: string;
};

type WelcomeScreenProps = {
  patientDisplayName: string;
  totalQuestions: number;
  /** True when any bundled instrument is parent/caregiver-report (e.g. PSC-17). */
  caregiverReportMode?: boolean;
  context?: WelcomeContext;
  onStart: () => void;
};

const firstName = (displayName: string): string => {
  const part = displayName.trim().split(/\s+/)[0];
  return part ?? displayName;
};

/**
 * Welcome and education screen before the linear assessment.
 * @agentic members
 */
export const WelcomeScreen = ({
  patientDisplayName,
  totalQuestions,
  caregiverReportMode = false,
  context,
  onStart,
}: WelcomeScreenProps) => {
  const fn = firstName(patientDisplayName);
  const advocate = context?.advocateDisplayName ?? getAdvocateDisplayName();
  const titleSuffix = context?.advocateTitle
    ? `, ${context.advocateTitle}`
    : "";
  const welcomeTitle = caregiverReportMode
    ? `Check-in for ${fn}`
    : `Welcome back, ${fn}`;
  const whatIsThisBody = caregiverReportMode
    ? "A brief questionnaire about your child's emotional and behavioral health."
    : "A quick check-in — like a thermometer for your mental health.";
  const startLabel = caregiverReportMode
    ? "Start questionnaire"
    : "Start check-in";
  const whoSeesBody = caregiverReportMode
    ? "Your Benchmark care team sees your answers. Your child's results may also be shared with their primary care provider (PCP) to coordinate their care."
    : "Your Benchmark care team sees your answers. Your results may also be shared with your primary care provider (PCP) to coordinate your care.";
  const privacyFooter = caregiverReportMode
    ? "🔒 Encrypted · shared with your care team; results may be shared with your child's PCP"
    : "🔒 Encrypted · shared with your care team; results may be shared with your PCP";

  return (
    <Paper radius="lg" p="xl" shadow="sm" className="welcome-card">
      <Stack gap="lg">
        <SurveyWordmark />
        <Stack gap="xs">
          <Title order={2} c="#391640">
            {welcomeTitle}
          </Title>
          {(context?.checkInNumber !== undefined ||
            context?.advocateDisplayName) && (
            <Text size="sm" style={{ opacity: 0.5, color: "#2a1a2e" }}>
              {context?.checkInNumber !== undefined
                ? `Check-in #${String(context.checkInNumber)}`
                : ""}
              {context?.checkInNumber !== undefined &&
              (context?.advocateDisplayName || advocate)
                ? " · "
                : ""}
              {context?.advocateDisplayName || advocate
                ? `from ${context?.advocateDisplayName ?? advocate}`
                : ""}
            </Text>
          )}
          {context?.advocateMessage && (
            <Box
              p="md"
              bg="plum.0"
              style={{ borderRadius: "var(--mantine-radius-md)" }}
            >
              <Text size="sm" fs="italic">
                &ldquo;{context.advocateMessage}&rdquo;
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                — {advocate}
                {titleSuffix}
              </Text>
            </Box>
          )}
        </Stack>

        <Divider />

        <Paper p="md" radius="md" withBorder shadow="none">
          <Stack gap="md">
            <Text
              size="xs"
              tt="uppercase"
              fw={600}
              style={{
                letterSpacing: "0.04em",
                opacity: 0.35,
                color: "#2a1a2e",
              }}
            >
              How this works
            </Text>
            <Stack gap="sm">
              <FaqBlock title="What is this?" body={whatIsThisBody} />
              <FaqBlock
                title="How long?"
                body={`About ${Math.max(1, Math.round(totalQuestions * 0.2))} minutes. ${totalQuestions} questions, one at a time.`}
              />
              <FaqBlock
                title="Will I see results?"
                body="Yes — right after you finish."
              />
              <FaqBlock title="Who sees this?" body={whoSeesBody} />
            </Stack>
          </Stack>
        </Paper>

        <Button size="lg" fullWidth onClick={onStart}>
          {startLabel}
        </Button>

        <Text size="xs" ta="center" style={{ opacity: 0.35, color: "#2a1a2e" }}>
          {privacyFooter}
        </Text>
      </Stack>
    </Paper>
  );
};

const FaqBlock = ({ title, body }: { title: string; body: string }) => (
  <Stack gap={4}>
    <Text size="sm" fw={600} c="#2a1a2e">
      {title}
    </Text>
    <Text size="sm" style={{ opacity: 0.55, color: "#2a1a2e" }}>
      {body}
    </Text>
  </Stack>
);
